import { useMemo } from "react";

import { BulkEntityActionItem } from "components/BulkActions/types";
import { useBulkDiscardRun } from "shared/Run/useDiscardRun";
import { useBulkSetRunPriority } from "shared/Run/useSetRunPriority";
import useCanDiscardRun from "shared/Run/access/useCanDiscardRun";
import useCanPrioritizeRun from "shared/Run/access/useCanPrioritizeRun";
import { RUN_PRIORITY_PRESET_ORDER, RUN_PRIORITY_PRESETS } from "shared/Run/priorityPresets";
import { AccessResult } from "utils/access";

import {
  BULK_ACTION_PER_PRIORITY_PRESET,
  BulkItemRunWithPosition,
  BulkWorkerPoolQueuedRunsActions,
} from "./types";

const useWorkerRunsBulkActions = (isPublicWorkerPool: boolean) => {
  const [runDiscard] = useBulkDiscardRun();
  const [runSetPriority] = useBulkSetRunPriority();
  const canDiscard = useCanDiscardRun();
  const canPrioritize = useCanPrioritizeRun();

  return useMemo<
    Array<BulkEntityActionItem<BulkWorkerPoolQueuedRunsActions, BulkItemRunWithPosition, undefined>>
  >(
    () => [
      ...RUN_PRIORITY_PRESET_ORDER.map((preset) => ({
        key: BULK_ACTION_PER_PRIORITY_PRESET[preset],
        title: RUN_PRIORITY_PRESETS[preset].label,
        resultTitle: `${RUN_PRIORITY_PRESETS[preset].label} runs results`,
        condition: (runWithPosition: BulkItemRunWithPosition): AccessResult => {
          if (runWithPosition.run.priorityPreset === preset) {
            return { result: false };
          }

          return canPrioritize({
            space: runWithPosition.space,
            isRunFinished: runWithPosition.run.finished,
            hasWorkerPool: true,
            isPublicWorkerPool,
          });
        },
        mutation: (run: BulkItemRunWithPosition) =>
          runSetPriority({ stack: run.stackId, run: run.id, preset }),
      })),
      {
        key: BulkWorkerPoolQueuedRunsActions.DISCARD,
        title: "Discard",
        resultTitle: "Discard runs results",
        condition: (runWithPosition) =>
          canDiscard(runWithPosition.run, {
            space: runWithPosition.space.id,
            spaceDetails: runWithPosition.space,
          }),
        mutation: (run) => runDiscard({ stack: run.stackId, run: run.id, note: null }),
      },
    ],
    [isPublicWorkerPool, runDiscard, runSetPriority, canDiscard, canPrioritize]
  );
};

export default useWorkerRunsBulkActions;
